import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertCircle, Plus, Loader2, Sparkles } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Questionnaire, Question } from '@/types/questionnaire';
import { HABIT_CATEGORY_ORDER } from '@/lib/constants/habitBadges';
import { QuestionEditor } from './QuestionEditor';
import { AIInstructionsDialog } from './AIInstructionsDialog';
import { updateQuestionnaire, validateQuestionnaire } from '@/lib/firebase/queries/questionnaires';
import { questionnaireAIService } from '@/lib/services/questionnaireAIService';
import { toast } from 'sonner';

interface EditQuestionnaireDialogProps {
  questionnaire: Questionnaire;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditQuestionnaireDialog({ questionnaire, open, onOpenChange }: EditQuestionnaireDialogProps) {
  const { t, dir } = useLanguage();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showInstructionsDialog, setShowInstructionsDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(HABIT_CATEGORY_ORDER[0]);

  useEffect(() => {
    setQuestions(questionnaire.questions);
  }, [questionnaire]);

  const addQuestion = (category: string) => {
    const newQuestion: Question = {
      id: crypto.randomUUID(),
      question: { en: '', ar: '' },
      options: [
        { id: crypto.randomUUID(), text: { en: '', ar: '' }, score: 1 },
        { id: crypto.randomUUID(), text: { en: '', ar: '' }, score: 0 }
      ],
      order: questions.filter(q => q.category === category).length,
      category: category as any
    };
    setQuestions([...questions, newQuestion]);
  };

  const removeQuestion = (questionId: string) => {
    setQuestions(questions.filter(q => q.id !== questionId));
  };

  const updateQuestion = (questionId: string, updates: Partial<Question>) => {
    setQuestions(questions.map(q => 
      q.id === questionId ? { ...q, ...updates } : q
    ));
  };

  const getQuestionsForCategory = (category: string) => {
    return questions.filter(q => q.category === category);
  };

  const getCategoryValidation = (category: string) => {
    const categoryQuestions = getQuestionsForCategory(category);
    return categoryQuestions.length > 0;
  };

  const generateAIQuestions = async (instructions: string) => {
    try {
      setIsGenerating(true);
      toast.info('🤖 AI is generating questions based on your instructions...');
      
      const generatedQuestions = await questionnaireAIService.generateQuestionsFromCustomInstructions(instructions);
      
      if (generatedQuestions.length > 0) {
        // Replace existing questions with generated ones
        setQuestions(generatedQuestions);
        toast.success(`✅ Generated ${generatedQuestions.length} questions based on your instructions!`);
      } else {
        toast.error('❌ Failed to generate questions. Please try again with different instructions.');
      }
    } catch (error) {
      console.error('Error generating AI questions:', error);
      toast.error('❌ Failed to generate questions. The AI service is unavailable — ask an administrator to check the server key.');
    } finally {
      setIsGenerating(false);
    }
  };

  const onSubmit = async () => {
    try {
      setIsSubmitting(true);

      const updatedQuestionnaire: Questionnaire = {
        ...questionnaire,
        questions
      };

      const validation = validateQuestionnaire(updatedQuestionnaire);
      if (!validation.isValid) {
        // A silent return here is what made the tester report "the questions
        // don't save": the dialog stayed open with no message, and closing it
        // discarded the work. Show the first few reasons, and where they are.
        const shown = validation.errors.slice(0, 4).map((e) => `${e.category}: ${e.message}`);
        const more = validation.errors.length - shown.length;
        toast.error(
          dir === 'rtl' ? 'لم يتم الحفظ — أكمل ما يلي' : 'Not saved — fix the following',
          { description: shown.join('\n') + (more > 0 ? `\n+${more} more` : ''), duration: 12000 },
        );
        return;
      }

      await updateQuestionnaire(questionnaire.id, { questions });
      if (validation.warnings.length > 0) {
        // Saved, but not yet usable for every category — say which ones.
        const missing = validation.warnings.map((w) => w.category).join(dir === 'rtl' ? '، ' : ', ');
        toast.warning(
          dir === 'rtl' ? 'تم الحفظ — الاستبانة غير مكتملة' : 'Saved — questionnaire is incomplete',
          { description: (dir === 'rtl' ? 'أقسام بلا أسئلة بعد: ' : 'Categories with no question yet: ') + missing, duration: 10000 },
        );
      } else {
        toast.success(dir === 'rtl' ? 'تم حفظ الاستبانة' : 'Questionnaire saved');
      }
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating questionnaire:', error);
      toast.error(
        dir === 'rtl' ? 'تعذّر حفظ الاستبانة' : 'Could not save the questionnaire',
        { description: error instanceof Error ? error.message : String(error), duration: 12000 },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-full h-[90vh] flex flex-col" dir={dir}>

        <div className="flex-1 overflow-hidden">
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)} className="w-full h-full flex flex-col" dir={dir}>
            <div className="flex items-center justify-center gap-4 mb-4">
              <TabsList className="grid w-4/5 grid-cols-6">
                {HABIT_CATEGORY_ORDER.map((category) => {
                  const isValid = getCategoryValidation(category);
                  
                  return (
                    <TabsTrigger key={category} value={category} className="flex items-center gap-2">
                      <span className="hidden sm:inline">
                        {t(`habits.categories.${category.toLowerCase()}`)}
                      </span>
                      {!isValid && <AlertCircle className="h-3 w-3 text-destructive" />}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
              
              <Button
                onClick={() => setShowInstructionsDialog(true)}
                disabled={isGenerating}
                size="sm"
                className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white"
              >
                {isGenerating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                {isGenerating ? 'Generating...' : 'AI Generate'}
              </Button>
            </div>

            <div className="mt-4 flex-1 overflow-hidden">
              {HABIT_CATEGORY_ORDER.map((category) => (
                <TabsContent key={category} value={category} className="h-full">
                  <div className="h-full flex flex-col">
                    {/* Validation Message */}
                    {!getCategoryValidation(category) && (
                      <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-4">
                        <div className="flex items-center gap-2 text-destructive">
                          <AlertCircle className="h-4 w-4" />
                          <span className="text-sm font-medium">
                            {t('dashboard.admin.questionnaires.categoryRequired')}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Scrollable Questions */}
                    <div className="flex-1 overflow-y-auto pe-2 space-y-4">
                      {getQuestionsForCategory(category).map((question) => (
                        <QuestionEditor
                          key={question.id}
                          question={question}
                          onUpdate={(updates) => updateQuestion(question.id, updates)}
                          onDelete={() => removeQuestion(question.id)}
                        />
                      ))}
                    </div>
                  </div>
                </TabsContent>
              ))}
            </div>
          </Tabs>
        </div>

        <div className="flex justify-between items-center gap-3 mt-2 pt-2 flex-shrink-0">
          <Button
            type="button"
            onClick={() => addQuestion(activeTab)}
            size="sm"
            variant="outline"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('dashboard.admin.questionnaires.addQuestion')}
          </Button>
          
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={onSubmit} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('dashboard.admin.questionnaires.save')}
            </Button>
          </div>
        </div>
      </DialogContent>

      <AIInstructionsDialog
        open={showInstructionsDialog}
        onOpenChange={setShowInstructionsDialog}
        onGenerate={generateAIQuestions}
        isGenerating={isGenerating}
      />
    </Dialog>
  );
}